// 🗂️ The Files folder (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.7, 09-29): every
// image, doc, drawing, note, code card and toy a room shared, from the backend's
// `getRoomArtifacts` read (justsomeguy-backend src/ChatGame/roomArtifacts.js).
// Pure helpers + the one fetch; the drawer is components/ChatGame/FilesFolder.js.
//
// The read is a POST on /api/takeGameStep so a private port's seat proof rides
// the BODY (a GET's URL is logged server side). Items arrive already shaped
// and whitelisted: { id, kind, title, url, summary, sender_name, format, at }.
// "Download list (.md)" is a client-side Markdown index of links (the
// memoryExport pattern); file bodies never ride along.

import { API_BASE_URL } from '../config/api';

export const PAGE_SIZE = 30;
export const MD_EXPORT_CAP = 600;
const MAX_EMPTY_HOPS = 4; // a rare chip in a huge room: each page reads a bounded window

// The chips (mockup room-toolkit §2). `kinds: null` = everything.
export const FILE_CHIPS = Object.freeze([
  { key: 'all', label: 'All', kinds: null },
  { key: 'docs', label: 'Docs', kinds: ['document', 'link'] },
  { key: 'images', label: 'Images', kinds: ['image'] },
  { key: 'video', label: 'Video', kinds: ['video'] },
  { key: 'drawings', label: 'Drawings', kinds: ['drawing'] },
  { key: 'notes', label: 'Notes', kinds: ['note'] },
  { key: 'code', label: 'Code', kinds: ['code', 'toy'] },
]);

export const KIND_LABEL = Object.freeze({
  image: 'Image', drawing: 'Drawing', document: 'Doc', link: 'Link',
  video: 'Video', note: 'Note', code: 'Code', toy: 'Toy',
});

export const chipByKey = (key) => FILE_CHIPS.find((c) => c.key === key) || FILE_CHIPS[0];

/** A chip's count from the server's per-kind counts, or null when unknown. */
export function chipCount(chip, counts) {
  if (!counts || typeof counts !== 'object') return null;
  if (!chip.kinds) return Number.isFinite(Number(counts.all)) ? Number(counts.all) : null;
  return chip.kinds.reduce((n, k) => n + (Number(counts[k]) || 0), 0);
}

/** Only chips with something under them (All always). Unknown or capped counts show every chip. */
export function visibleChips(counts, capped = false) {
  if (!counts || capped) return FILE_CHIPS;
  return FILE_CHIPS.filter((c) => !c.kinds || chipCount(c, counts) > 0);
}

/** Only http(s) ever becomes a link (the server says so too; this is the second lock). */
export function safeHref(url) {
  return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null;
}

/** A YouTube still for a video row, or null. */
export function youtubeThumb(url) {
  const m = typeof url === 'string' && url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? `https://i.ytimg.com/vi/${m[1]}/mqdefault.jpg` : null;
}

/** "just now" · "5m ago" · "2h ago" · "Yesterday" · "Sep 27" · "Sep 27, 2025". */
export function whenLabel(at, now = new Date()) {
  const t = Date.parse(at || '');
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  const mins = Math.floor((now.getTime() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return `${Math.floor(mins / 60)}h ago`;
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  const opts = d.getFullYear() === now.getFullYear()
    ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString('en-US', opts);
}

const oneLine = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim();

export function artifactsFileName(roomName, now = new Date()) {
  const slug = oneLine(roomName).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'room';
  return `${slug}-files-${now.toISOString().slice(0, 10)}.md`;
}

// Markdown-safe text for a link label / a line, and a URL that can't break out of ( ).
const mdText = (s) => oneLine(s).replace(/([\\`*_[\]<>])/g, '\\$1');
// (encodeURIComponent leaves ( ) alone, and a bare ) ends a Markdown link early.)
const mdUrl = (u) => String(u).replace(/[()\s<>]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`);
const dayOf = (at) => {
  const t = Date.parse(at || '');
  return Number.isFinite(t) ? new Date(t).toISOString().slice(0, 10) : '';
};

// The file's sections, in reading order. Each item lands in the first that takes its kind.
const MD_SECTIONS = [
  { title: 'Docs', kinds: ['document'] },
  { title: 'Links', kinds: ['link'] },
  { title: 'Images', kinds: ['image'] },
  { title: 'Drawings', kinds: ['drawing'] },
  { title: 'Video', kinds: ['video'] },
  { title: 'Notes', kinds: ['note'] },
  { title: 'Code and toys', kinds: ['code', 'toy'] },
];

/** The folder as one readable Markdown file: names, who, when, a line on each, and the link. */
export function artifactsToMarkdown(roomName, items, { now = new Date(), capped = false } = {}) {
  const list = (Array.isArray(items) ? items : []).filter((it) => it && it.id);
  const room = oneLine(roomName) || 'This room';
  const out = [
    `# Files from ${mdText(room)} (myspAIce)`,
    '',
    `Exported ${now.toISOString().slice(0, 10)}. ${list.length} file${list.length === 1 ? '' : 's'}, newest first in each group.`,
    'Each link opens the shared file. Links are hard to guess, not secret.',
  ];
  if (capped) out.push(`This is the newest ${list.length}. Older files are not in this list.`);
  if (!list.length) {
    out.push('', 'Nothing shared yet.');
  }
  for (const sec of MD_SECTIONS) {
    const rows = list.filter((it) => sec.kinds.includes(it.kind));
    if (!rows.length) continue;
    out.push('', `## ${sec.title}`, '');
    for (const it of rows) {
      const href = safeHref(it.url);
      const name = mdText(it.title || KIND_LABEL[it.kind] || 'File');
      const bits = [href ? `[${name}](${mdUrl(href)})` : name];
      if (!href && (it.kind === 'code' || it.kind === 'toy')) bits.push('(runs in the room)');
      if (it.sender_name) bits.push(`· ${mdText(it.sender_name)}`);
      const day = dayOf(it.at);
      if (day) bits.push(`· ${day}`);
      out.push(`- ${bits.join(' ')}`);
      if (it.summary) out.push(`  ${mdText(it.summary)}`);
    }
  }
  out.push('', '---', 'Other people’s words are in here too. Be kind with them.');
  return `${out.join('\n')}\n`;
}

/** Hand the viewer a text file (Blob + a temporary link). */
export function downloadText(fileName, text, type = 'text/markdown;charset=utf-8') {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The seat proof a private port needs, or {} (never a URL param). */
function proofOf(viewer) {
  if (!viewer || typeof viewer !== 'object') return {};
  const id = viewer.player_id || viewer.playerId || null;
  const secret = viewer.player_secret || viewer.playerSecret || null;
  if (!id) return {};
  return { player_id: String(id), ...(secret ? { player_secret: String(secret) } : {}) };
}

/**
 * One page from the server, normalized:
 *   { ok: true, items, hasMore, next, counts, countsCapped, roomName }
 *   { ok: false, private: true } | { ok: false, error }
 * Never throws for a refusal; a network failure throws (the caller's fail state).
 */
export async function fetchRoomArtifacts({ gameId, viewer = null, kinds = null, before = null, limit = PAGE_SIZE, counts = false } = {}, fetchImpl = fetch) {
  const body = {
    getRoomArtifacts: true,
    gameInstanceId: gameId,
    limit,
    ...(Array.isArray(kinds) && kinds.length ? { kinds: kinds.join(',') } : {}),
    ...(before ? { before } : {}),
    ...(counts ? { counts: true } : {}),
    ...proofOf(viewer),
  };
  const res = await fetchImpl(`${API_BASE_URL}/api/takeGameStep`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (data && data.space_private) return { ok: false, private: true };
  if (!res.ok || !data || data.success !== true) {
    return { ok: false, error: (data && data.error) || `HTTP ${res.status}` };
  }
  return {
    ok: true,
    items: Array.isArray(data.items) ? data.items : [],
    hasMore: !!data.has_more,
    next: data.next_before || null,
    counts: data.counts || null,
    countsCapped: !!data.counts_capped,
    roomName: data.room_name || null,
  };
}

/**
 * A page with at least one item when the room has one: a filter chip over a
 * huge room can answer an empty page with has_more (the server reads a bounded
 * window per call), so hop on a few times before saying "nothing here".
 */
export async function fetchRoomArtifactsPage(opts, list = fetchRoomArtifacts) {
  let r = await list(opts);
  let hops = 1;
  while (r && r.ok && r.items.length === 0 && r.hasMore && r.next && hops < MAX_EMPTY_HOPS) {
    const more = await list({ ...opts, before: r.next, counts: false });
    if (!more || !more.ok) break;
    r = { ...r, items: more.items, hasMore: more.hasMore, next: more.next };
    hops += 1;
  }
  return r;
}

/** Every file, page by page, for the Markdown list (newest `cap` at most). */
export async function fetchAllRoomArtifacts({ gameId, viewer = null } = {}, list = fetchRoomArtifacts, cap = MD_EXPORT_CAP) {
  const items = [];
  const seen = new Set();
  let before = null;
  let roomName = null;
  for (let guard = 0; guard < 200; guard += 1) {
    const r = await list({ gameId, viewer, before, limit: 60 });
    if (!r || !r.ok) throw new Error(r && r.private ? 'private' : ((r && r.error) || 'failed'));
    roomName = roomName || r.roomName;
    for (let i = 0; i < r.items.length; i += 1) {
      const it = r.items[i];
      if (!it || seen.has(it.id)) continue;
      seen.add(it.id);
      items.push(it);
      if (items.length >= cap) return { items, capped: i < r.items.length - 1 || r.hasMore, roomName };
    }
    if (!r.hasMore || !r.next) break;
    before = r.next;
  }
  return { items, capped: false, roomName };
}
